import { Routes } from '@angular/router';
import { authGuard } from '@sp-client/app/guards/auth.guard';
import { onboardGuard } from '@sp-client/app/guards/onboard.guard';
import { DesktopOnlyGuard } from './guards/desktop-only.guard';
import { MobileOnlyGuard } from './guards/mobile-only.guard';
import { RedirectToPanelGuard } from './guards/redirect-panel-guard';

const siteTitle = 'Scoutly Plans';

const protectedRoutes: Routes = [
	{
		path: '',
		title: `${siteTitle} Dashboard`,
		data: { showTabbar: true },
		loadComponent: async () => (await import('@sp-client/app/pages/dashboard/dashboard.component')).DashboardComponent
	},
	{
		path: 'dashboard',
		title: `${siteTitle} Dashboard`,
		redirectTo: '',
		pathMatch: 'full',
		loadComponent: async () => (await import('@sp-client/app/pages/dashboard/dashboard.component')).DashboardComponent,
		data: { showTabbar: true }
	},
	//mobile routes
	{
		path: 'itinerary',
		title: `${siteTitle} Itinerary`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/itinerary/itinerary.component')).ItineraryComponent,
		data: { showTabbar: true }
	},
	{
		path: 'itinerary/:id',
		title: `${siteTitle} Itinerary`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/itinerary/itinerary.component')).ItineraryComponent,
		data: { showTabbar: true }
	},
	{
		path: 'visas',
		title: `${siteTitle} Visas`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/visas/visas.component')).VisasComponent
	},
	{
		path: 'visas/:id',
		title: `${siteTitle} Visas`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/visas/visas.component')).VisasComponent
	},
	{
		path: 'journal',
		title: `${siteTitle} Journal`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/journal/journal.component')).JournalComponent,
		data: { showTabbar: true }
	},
	{
		path: 'favorites',
		title: `${siteTitle} Favorites`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/favorites/favorites.component')).FavoritesComponent,
		data: { showTabbar: true }
	},
	{
		path: 'neighborhoods',
		title: `${siteTitle} Neighborhood Guides`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/neighborhood-guides/neighborhood-guides.component'))
				.NeighborhoodGuidesComponent
	},
	{
		path: 'neighborhoods/:city',
		title: `${siteTitle} Neighborhood Guides`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/neighborhood-guides/neighborhood-guides.component'))
				.NeighborhoodGuidesComponent
	},
	{
		path: 'neighborhood/:id',
		title: `${siteTitle} Neighborhood Guide`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/neighborhood-guides/neighborhood-guide/neighborhood-guide.component'))
				.NeighborhoodGuideComponent
	},
	{
		path: 'country',
		title: `${siteTitle} Country Guide`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/country-guide/country-guide.component')).CountryGuideComponent
	},
	{
		path: 'country/:id',
		title: `${siteTitle} Country Guide`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/country-guide/country-guide.component')).CountryGuideComponent
	},
	{
		path: 'trips',
		title: `${siteTitle} Day Trip Guide`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/day-trips/day-trips.component')).DayTripsComponent
	},
	{
		path: 'trips/:id',
		title: `${siteTitle} Day Trip Guide`,
		canActivate: [RedirectToPanelGuard],
		canMatch: [MobileOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/day-trips/day-trips.component')).DayTripsComponent
	},
	{
		path: 'styleguide',
		title: `${siteTitle} Style Guide`,
		loadComponent: async () =>
			(await import('@sp-client/app/pages/style-guide/style-guide.component')).StyleGuideComponent,
		data: { showSidebar: false, preload: false }
	},
	{
		path: 'chat',
		title: `${siteTitle}`,
		loadComponent: async () => (await import('@sp-client/app/pages/chat/chat.component')).ChatComponent
	},
	{
		path: 'settings',
		title: `${siteTitle} Settings`,
		loadComponent: async () => (await import('@sp-client/app/pages/settings/settings.component')).SettingsComponent
	},
	{
		path: 'store',
		title: `${siteTitle} Store`,
		loadComponent: async () => (await import('@sp-client/app/pages/store/store.component')).StoreComponent,
		data: { showSidebar: false }
	},

	//Desktop routes
	{
		path: 'itinerary',
		title: `${siteTitle} Itinerary`,
		outlet: 'panel',
		data: { isPanel: true },
		canMatch: [DesktopOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/itinerary/itinerary.component')).ItineraryComponent
	},
	{
		path: 'visas',
		title: `${siteTitle} Visas`,
		outlet: 'panel',
		data: { isPanel: true },
		canMatch: [DesktopOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/visas/visas.component')).VisasComponent
	},
	{
		path: 'visas/:id',
		title: `${siteTitle} Visas`,
		outlet: 'panel',
		data: { isPanel: true },
		canMatch: [DesktopOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/visas/visas.component')).VisasComponent
	},
	{
		path: 'journal',
		title: `${siteTitle} Journal`,
		outlet: 'panel',
		data: { isPanel: true },
		canMatch: [DesktopOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/journal/journal.component')).JournalComponent
	},
	{
		path: 'favorites',
		title: `${siteTitle} Favorites`,
		outlet: 'panel',
		data: { isPanel: true },
		canMatch: [DesktopOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/favorites/favorites.component')).FavoritesComponent
	},
	{
		path: 'neighborhoods',
		title: `${siteTitle} Neighborhood Guides`,
		outlet: 'panel',
		data: { isPanel: true },
		canMatch: [DesktopOnlyGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/neighborhood-guides/neighborhood-guides.component'))
				.NeighborhoodGuidesComponent
	},
	{
		path: 'neighborhoods/:city',
		title: `${siteTitle} Neighborhood Guides`,
		outlet: 'panel',
		data: { isPanel: true },
		canMatch: [DesktopOnlyGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/neighborhood-guides/neighborhood-guides.component'))
				.NeighborhoodGuidesComponent
	},
	{
		path: 'neighborhood/:id',
		title: `${siteTitle} Neighborhood Guide`,
		outlet: 'panel',
		data: { isPanel: true },
		canMatch: [DesktopOnlyGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/neighborhood-guides/neighborhood-guide/neighborhood-guide.component'))
				.NeighborhoodGuideComponent
	},
	{
		path: 'country',
		title: `${siteTitle} Country Guide`,
		outlet: 'panel',
		data: { isPanel: true },
		canMatch: [DesktopOnlyGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/country-guide/country-guide.component')).CountryGuideComponent
	},
	{
		path: 'country/:id',
		title: `${siteTitle} Country Guide`,
		outlet: 'panel',
		data: { isPanel: true },
		canMatch: [DesktopOnlyGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/country-guide/country-guide.component')).CountryGuideComponent
	},
	{
		path: 'trips',
		title: `${siteTitle} Day Trip Guide`,
		outlet: 'panel',
		data: { isPanel: true },
		canMatch: [DesktopOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/day-trips/day-trips.component')).DayTripsComponent
	},
	{
		path: 'trips/:id',
		title: `${siteTitle} Day Trip Guide`,
		outlet: 'panel',
		data: { isPanel: true },
		canMatch: [DesktopOnlyGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/day-trips/day-trips.component')).DayTripsComponent
	}
];

const authRoutes: Routes = [
	{
		path: 'signup',
		title: `${siteTitle} Signup`,
		loadComponent: async () => (await import('@sp-client/app/pages/auth/signup/signup.component')).SignUpComponent
	},
	{
		path: 'signup-email',
		title: `${siteTitle} Signup`,
		loadComponent: async () =>
			(await import('@sp-client/app/pages/auth/signup-email/signup-email.component')).SignUpEmailComponent
	},
	{
		path: 'password-reset',
		title: `${siteTitle} Password Reset`,
		loadComponent: async () =>
			(await import('@sp-client/app/pages/auth/password-reset/password-reset.component')).PasswordResetComponent
	},
	{
		path: 'forgot-password',
		title: `${siteTitle} Forgot Password`,
		loadComponent: async () =>
			(await import('@sp-client/app/pages/auth/forgot-password/forgot-password.component')).ForgotPasswordComponent
	},
	{
		path: 'verify',
		title: `${siteTitle}`,
		loadComponent: async () => (await import('@sp-client/app/pages/auth/verify/verify.component')).VerifyComponent
	},
	{
		path: 'login',
		title: `${siteTitle} Login`,
		loadComponent: async () => (await import('@sp-client/app/pages/auth/login/login.component')).LoginComponent
	},
	{
		path: 'logout',
		title: `${siteTitle} Logout`,
		loadComponent: async () => (await import('@sp-client/app/pages/auth/logout/logout.component')).LogoutComponent
	},
	{
		path: 'onboard',
		title: `${siteTitle} Onboard`,
		canActivate: [onboardGuard],
		loadComponent: async () => (await import('@sp-client/app/pages/onboard/onboard.component')).OnboardComponent
	},
	{
		path: 'onboard/country',
		title: `${siteTitle} Onboard Country`,
		canActivate: [onboardGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/onboard/onboard-country/onboard-country.component')).OnboardCountryComponent
	},
	{
		path: 'onboard/region',
		title: `${siteTitle} Onboard Region`,
		canActivate: [onboardGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/onboard/onboard-region/onboard-region.component')).OnboardRegionComponent
	},
	{
		path: 'onboard/demographic',
		title: `${siteTitle} Onboard Demographic`,
		canActivate: [onboardGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/onboard/onboard-demographic/onboard-demographic.component'))
				.OnboardDemographicComponent
	},
	{
		path: 'onboard/review',
		title: `${siteTitle} Onboard Review`,
		canActivate: [onboardGuard],
		loadComponent: async () =>
			(await import('@sp-client/app/pages/onboard/onboard-review/onboard-review.component')).OnboardReviewComponent
	}
];

export const appRoutes: Routes = [
	...protectedRoutes.map(route => ({
		...route,
		canActivate: [authGuard]
	})),
	...authRoutes.map(route => ({
		...route,
		data: { showSidebar: false }
	})),
	{ path: '**', redirectTo: '' }
];
